import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, Mic, Square, Trash2, Upload, Send } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';

const compressImage = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1024;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round((height * MAX) / width); width = MAX; }
          else { width = Math.round((width * MAX) / height); height = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.75));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

const SendReportPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [weight, setWeight] = useState('');
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [audio, setAudio] = useState(null);
  const [recording, setRecording] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);

  const handlePhotoAdd = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 4 - photos.length;
    const toProcess = files.slice(0, remaining);
    const compressed = await Promise.all(toProcess.map(compressImage));
    setPhotos((prev) => [...prev, ...compressed]);
    e.target.value = '';
  }, [photos]);

  const handlePhotoRemove = (idx) => setPhotos((prev) => prev.filter((_, i) => i !== idx));

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onload = (ev) => setAudio(ev.target.result);
        reader.readAsDataURL(blob);
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch {
      setError(t('reports.mic_error'));
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const handleAudioFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setAudio(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    setError('');
    if (photos.length < 2) { setError(t('reports.error_min_photos')); return; }
    setSending(true);
    try {
      const res = await apiFetch('/api/reports', {
        method: 'POST',
        body: JSON.stringify({
          weight: weight ? parseFloat(weight) : null,
          notes: notes.trim() || null,
          photos,
          audio,
        }),
      });
      if (res.ok) {
        navigate('/reports', { replace: true });
      } else {
        const d = await res.json();
        setError(d.detail || t('common.error_connection'));
      }
    } catch {
      setError(t('common.error_connection'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="page-container animate-fade-in">
      {/* Header */}
      <div className="header" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            className="btn btn-icon btn-secondary"
            onClick={() => navigate(-1)}
            style={{ flexShrink: 0 }}
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <p className="text-muted" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {t('reports.inbox_title')}
            </p>
            <h1 className="header-title">{t('reports.send_title')}</h1>
          </div>
        </div>
        <img src="/dragon-logo.png" alt="DragonFit" className="header-logo" />
      </div>

      {/* Peso */}
      <div className="form-group">
        <label className="form-label">{t('reports.weight_label')}</label>
        <input
          type="number"
          className="input"
          placeholder="80.5"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
          step="0.1"
          inputMode="decimal"
        />
      </div>

      {/* Fotos */}
      <div className="form-group">
        <label className="form-label">
          {t('reports.photos_label')}
          <span style={{ color: 'var(--muted-foreground)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
            {' '}{t('reports.photos_hint')}
          </span>
        </label>
        <div className="report-photos-grid">
          {photos.map((src, i) => (
            <div key={i} className="report-photo-thumb">
              <img src={src} alt="" />
              <button className="report-photo-remove" onClick={() => handlePhotoRemove(i)}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {photos.length < 4 && (
            <button className="report-photo-add" onClick={() => fileInputRef.current?.click()}>
              <Camera size={24} />
              <span>{t('reports.add_photo')}</span>
            </button>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handlePhotoAdd}
        />
        <p style={{ fontSize: '12px', color: 'var(--muted-foreground)', marginTop: '8px' }}>
          {photos.length}/4 {photos.length < 2 ? `· ${t('reports.error_min_photos')}` : ''}
        </p>
      </div>

      {/* Notas */}
      <div className="form-group">
        <label className="form-label">{t('reports.notes_label')}</label>
        <textarea
          className="input"
          rows={5}
          placeholder={t('reports.notes_placeholder')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          style={{ resize: 'vertical', lineHeight: '1.6' }}
        />
      </div>

      {/* Audio */}
      <div className="form-group">
        <label className="form-label">
          {t('reports.audio_label')}
          <span style={{ color: 'var(--muted-foreground)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
            {' '}{t('common.optional')}
          </span>
        </label>

        {audio ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <audio src={audio} controls style={{ flex: 1, height: '40px' }} />
            <button className="btn btn-sm btn-secondary" onClick={() => setAudio(null)}>
              <Trash2 size={14} />
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className={`btn btn-sm ${recording ? 'btn-danger' : 'btn-secondary'}`}
              onClick={recording ? stopRecording : startRecording}
              style={{ flex: 1 }}
            >
              {recording
                ? <><Square size={14} /> {t('reports.stop_rec')}</>
                : <><Mic size={14} /> {t('reports.start_rec')}</>
              }
            </button>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => audioInputRef.current?.click()}
              style={{ flex: 1 }}
            >
              <Upload size={14} /> {t('reports.upload_audio')}
            </button>
            <input
              ref={audioInputRef}
              type="file"
              accept="audio/*"
              style={{ display: 'none' }}
              onChange={handleAudioFile}
            />
          </div>
        )}

        {recording && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
            <span style={{
              width: '8px', height: '8px', borderRadius: '50%',
              background: '#ef4444', display: 'inline-block',
              animation: 'pulse 1s infinite',
            }} />
            <span style={{ fontSize: '13px', color: '#ef4444' }}>{t('reports.recording')}</span>
          </div>
        )}
      </div>

      {error && (
        <p style={{ color: '#ef4444', fontSize: '14px', marginBottom: '16px', padding: '12px', background: 'rgba(239,68,68,0.1)', borderRadius: '8px', border: '1px solid rgba(239,68,68,0.3)' }}>
          {error}
        </p>
      )}

      {/* Botones */}
      <div style={{ display: 'flex', gap: '10px', paddingBottom: '24px' }}>
        <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => navigate(-1)}>
          {t('common.cancel')}
        </button>
        <button
          className="btn btn-primary"
          style={{ flex: 2 }}
          onClick={handleSubmit}
          disabled={sending || photos.length < 2}
        >
          {sending
            ? t('reports.sending')
            : <><Send size={16} /> {t('reports.send_btn')}</>
          }
        </button>
      </div>
    </div>
  );
};

export default SendReportPage;
